import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { DateTime } from 'luxon';
import { startCase } from 'lodash';

import { Panel } from '@shared/components/bootstrap/panel';
import { Picture } from '@shared/components/helpers';
import { Lightbox } from '@shared/components/helpers/lightbox';
import { UserRole } from '@shared/types/user_role';
import { ImgixBG, ImgixFit } from '@shared/utils/imgix';

import { Preview as AddressPreview } from '@admin/components/address/preview';
import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { Roles } from '@admin/components/helpers/roles';
import { ItemBrick } from '@admin/components/items/item_brick';
import { Pagination } from '@admin/components/pagination';
import { QaQrCodeLink } from '@admin/components/qa_automation/qa_qr_code_link';
import { Spinner } from '@admin/components/spinner';
import { CreateTicket as CreateTechSupportTicket } from '@admin/components/tickets/tech_support/create_ticket';
import { accountURL, editTransferURL, palletURL, transfersURL } from '@admin/config/routes';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  History__Subject,
  PalletGradeScore,
  PalletStateEnum,
  Ticket__TicketableEnum,
  TransferDetailsQuery,
  TransferState,
  useTransferDetailsQuery,
} from '@admin/schema';
import { IItem } from '@admin/types/item';

import { HistoriesPanel } from '../histories/panel';
import { TransferStateLabel } from './state_label';

const PALLET_PAGE_PER = 25;

type TransferPallet = TransferDetailsQuery['transfer']['paginatedPallets']['results'][number];
type TransferPalletItem = TransferPallet['items'][number];

const csrfToken = (): string => document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';

const transferStateLabelClass = (state: TransferState): string => {
  switch (state) {
    case TransferState.Canceled:
      return 'label-danger';
    case TransferState.Completed:
      return 'label-info';
    default:
      return 'label-default';
  }
};

const palletGradeLabelClass = (score: PalletGradeScore): string => {
  switch (score) {
    case PalletGradeScore.Good:
      return 'label-success';
    case PalletGradeScore.Acceptable:
      return 'label-warning';
    case PalletGradeScore.Fail:
      return 'label-danger';
    default:
      return 'label-default';
  }
};

const formatPalletState = (state: PalletStateEnum): string => startCase(state.toLowerCase().replace(/_/g, ' '));

const formatItemState = (state: string): string => startCase(state.toLowerCase().replace(/_/g, ' '));

const toItemBrickItem = (item: TransferPalletItem): IItem => ({
  id: Number(item.id),
  hero: item.hero,
  barcode: { id: 0, value: item.barcode.value },
  state: formatItemState(item.state),
  images: item.images.map((img) => ({
    id: Number(img.id),
    state: img.state.toLowerCase(),
    source: img.source,
    size: img.size,
    name: '',
    createdAt: '',
  })),
  account_id: Number(item.account.id),
  accountID: Number(item.account.id),
});

const DENSITY_TYPES = new Set(['Single', 'Double', 'Stackable Crate']);

const primaryAccountsFromPallet = (pallet: TransferPallet) => {
  const byId = new Map<string, { id: string; customerName: string }>();
  pallet.items.forEach((item) => {
    const id = item.account.id;
    if (!byId.has(id)) {
      byId.set(id, { id, customerName: item.account.customer.name });
    }
  });
  return Array.from(byId.values());
};

export const TransferDetails: React.FC = () => {
  useTitle('Transfer');
  const { id } = useParams<{ id: string }>();
  const transferId = String(id);
  const [palletsPage, setPalletsPage] = useState<number>(1);
  const [gradeLightboxUrl, setGradeLightboxUrl] = useState<string | undefined>();
  const { confirmation, confirm } = useConfirmation();

  const { data, loading, error, refetch } = useTransferDetailsQuery({
    client,
    variables: { id, palletsPage, palletsPer: PALLET_PAGE_PER },
    fetchPolicy: 'cache-and-network',
  });

  const onCancel = useCallback(async () => {
    const confirmed = await confirm({
      title: 'Are you sure you want to cancel this transfer?',
      description: 'Canceling a transfer may generate a "restock" transfer if the transfer is "enroute".',
      confirm: 'Yes, Cancel This Transfer',
      cancel: 'No, Nevermind',
    });
    if (confirmed !== ConfirmationResponse.Confirm) return;

    try {
      const response = await fetch(`/transfers/${transferId}/cancel.json`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
      });
      if (!response.ok) {
        window.alert('Could not cancel this transfer. Please try again or contact engineering.');
        return;
      }
      await refetch();
    } catch (err) {
      console.error('Failed to cancel transfer', err);
      window.alert('Could not cancel this transfer. Please try again or contact engineering.');
    }
  }, [confirm, transferId, refetch]);

  if (loading && !data) {
    return <Spinner />;
  }
  if (error || !data?.transfer) {
    return (
      <div className="alert alert-danger">
        {error ? `Could not load transfer: ${error.message}` : 'Transfer not found.'}
      </div>
    );
  }

  const transfer = data.transfer;
  const paginated = transfer.paginatedPallets;

  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}

      <div className="text-overflow pad-top pad-btm">
        <ol className="breadcrumb page-header pull-left">
          <li>
            <NavLink to={transfersURL()}>Transfers</NavLink>
          </li>
          <li className="active">Transfer #{transfer.id}</li>
        </ol>
        <div className="pull-right">
          <Roles show={[UserRole.Admin, UserRole.L2Agent, UserRole.Manager]}>
            <CreateTechSupportTicket
              ticketable={{
                id: transfer.id,
                type: Ticket__TicketableEnum.Transfer,
                displayName: `Transfer #${transfer.id}`,
              }}
            />
          </Roles>
        </div>
        <div className="clearfix" />
      </div>

      <Panel>
        <Panel.Header>
          <Panel.Title>Details</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <p>
            <strong>ID:</strong> <span>{transfer.id}</span>
          </p>
          <p>
            <strong>State:</strong> <TransferStateLabel transfer={transfer} />
          </p>
          <p>
            <strong>Created At:</strong>{' '}
            <span>{DateTime.fromISO(transfer.scheduled).toLocaleString(DateTime.DATETIME_MED)}</span>
          </p>
          <p>
            <strong>Created By:</strong> <span>{transfer.user.name}</span>
          </p>
          <p>
            <strong>Origin:</strong> <span>{transfer.origin.name}</span>{' '}
            <span>
              (<AddressPreview address={transfer.origin.address} mode="inline" />)
            </span>
          </p>
          <p>
            <strong>Destination:</strong> <span>{transfer.destination.name}</span>{' '}
            <span>
              (<AddressPreview address={transfer.destination.address} mode="inline" />)
            </span>
          </p>
          <p>
            <strong>Vehicle:</strong> <span>{transfer.vehicle ? transfer.vehicle.serialCode : ''}</span>
          </p>
          <p>
            <strong>Pallet Heights:</strong> <span>{transfer.palletHeightCounts}</span>
          </p>
          <p>
            <strong>Cuft:</strong> <span>{transfer.cuft}</span>
          </p>
        </Panel.Body>
        <Panel.Footer align="right">
          {transfer.cancelable && (
            <button type="button" className="btn btn-danger" onClick={() => void onCancel()}>
              Cancel
            </button>
          )}{' '}
          {transfer.state === TransferState.Approved && (
            <a className="btn btn-primary" href={editTransferURL({ id: transfer.id })}>
              Edit
            </a>
          )}
        </Panel.Footer>
      </Panel>

      <HistoriesPanel type={History__Subject.Transfer} id={transfer.id} />

      {paginated.results.map((pallet) => {
        const primaryAccounts = primaryAccountsFromPallet(pallet);
        const showHeightLabel = pallet.size !== 'oversized' && Boolean(pallet.height);
        return (
          <div key={pallet.id}>
            <Panel>
              <Panel.Body>
                <div className="pull-left">
                  <div className="pull-left panel-pallet-image">
                    {pallet.grade?.image && (
                      <button
                        type="button"
                        className="btn-link"
                        style={{ padding: 0, border: 'none', background: 'none' }}
                        aria-label="View pallet grade image"
                        onClick={() => setGradeLightboxUrl(pallet.grade?.image?.source)}
                      >
                        <figure>
                          <Picture
                            source={pallet.grade.image.source}
                            alt=""
                            width={50}
                            height={50}
                            fit={ImgixFit.Fill}
                            background={ImgixBG.White}
                          />
                        </figure>
                      </button>
                    )}
                  </div>
                  <div className="pull-right">
                    <a className="btn-link" href={palletURL(pallet.id)}>
                      #{pallet.barcode.value}
                    </a>{' '}
                    <strong>({formatPalletState(pallet.state)})</strong>{' '}
                    {pallet.grade && (
                      <span className={`label ${palletGradeLabelClass(pallet.grade.score)}`}>
                        {startCase(pallet.grade.score.toLowerCase())}
                      </span>
                    )}
                    <QaQrCodeLink value={pallet.barcode.value} />
                    <br />
                    <span>
                      {pallet.type.name} ({pallet.itemsCount} {pallet.itemsCount === 1 ? 'Item' : 'Items'})
                    </span>{' '}
                    {DENSITY_TYPES.has(pallet.type.name) && (
                      <span
                        className={`label ${pallet.densityMetrics.percentage >= 75 ? 'label-success' : 'label-danger'}`}
                      >
                        Density: {pallet.densityMetrics.percentage.toFixed(2)}%
                      </span>
                    )}{' '}
                    {showHeightLabel && <span className="label label-default">Height: {pallet.height!.value} in.</span>}
                  </div>
                </div>
                <div className="pull-right">
                  {(pallet.location || pallet.state === PalletStateEnum.Enroute) && (
                    <div>
                      <strong>{primaryAccounts.length === 1 ? 'Account:' : 'Accounts:'}</strong>{' '}
                      {primaryAccounts.map((a, i) => (
                        <span key={a.id}>
                          {i > 0 && ', '}
                          <a className="btn-link" href={accountURL({ id: a.id })}>
                            {a.customerName}
                          </a>
                        </span>
                      ))}
                    </div>
                  )}
                  {pallet.location ? (
                    <>
                      <div>
                        <strong>Warehouse:</strong> <span>{pallet.location.warehouse.name}</span>
                      </div>
                      <div>
                        <strong>Location:</strong>{' '}
                        <a className="text-primary" href={`/locations/${pallet.location.id}`}>
                          #{pallet.location.value}
                        </a>{' '}
                        <QaQrCodeLink value={pallet.location.value} />
                      </div>
                    </>
                  ) : (
                    <div>
                      <strong>Location:</strong> <span>On Transfer Truck</span>
                    </div>
                  )}
                </div>
                <div className="clearfix" />
              </Panel.Body>
            </Panel>

            <div className="grid">
              <div className="row">
                {pallet.items.map((item) => (
                  <div key={item.id} className="entry col-xs-6 col-sm-4 col-md-3 col-lg-2">
                    <ItemBrick item={toItemBrickItem(item)} sizeable />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      {paginated.pagination.totalPages > 1 && (
        <Pagination pagination={paginated.pagination} onPage={(page) => setPalletsPage(page)} />
      )}

      {gradeLightboxUrl && <Lightbox imageURL={gradeLightboxUrl} onClose={() => setGradeLightboxUrl(undefined)} />}
    </>
  );
};
